import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Navbar from '../components/Navbar';

// Master ledger — commission earned from tenant payments (admin only)

const BAR = '#38bdf8';
const panel = { background: 'rgba(15,23,42,0.92)', borderRadius: 16, border: '1px solid rgba(56,189,248,0.15)', padding: 24 };
const th = { padding: '10px 14px', textAlign: 'left', color: '#cbd5e1', fontSize: 13, letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 700, whiteSpace: 'nowrap' };
const td = { padding: '10px 14px', color: '#e2e8f0', fontSize: 14.5, whiteSpace: 'nowrap' };
const num = { textAlign: 'right' };
const input = { background: '#ffffff', border: '1.5px solid #94a3b8', borderRadius: 10, padding: '8px 12px', color: '#0f172a', fontSize: 14.5, fontFamily: 'inherit', colorScheme: 'light' };

const kes = (n, dp = 0) => `KES ${Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
const compact = (n) => (n >= 1e6 ? `${+(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${+(n / 1e3).toFixed(1)}K` : `${n}`);
const monthShort = (ym) => new Date(`${ym}-01T12:00:00`).toLocaleDateString('en-KE', { month: 'short', year: '2-digit' });
const todayEAT = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Nairobi' }).format(new Date());

const presets = () => {
  const t = todayEAT();
  const [y, m] = t.split('-').map(Number);
  const pad = (v) => String(v).padStart(2, '0');
  const lastOfPrev = new Date(Date.UTC(y, m - 1, 0)).toISOString().slice(0, 10);
  return {
    'This month': { from: `${y}-${pad(m)}-01`, to: t },
    'Last month': { from: `${lastOfPrev.slice(0, 7)}-01`, to: lastOfPrev },
    'This year': { from: `${y}-01-01`, to: t },
  };
};

function MonthTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: '#0f172a', border: '1px solid rgba(56,189,248,0.3)', borderRadius: 8, padding: '8px 12px' }}>
      <div style={{ color: '#e2e8f0', fontSize: 13.5, marginBottom: 4 }}>{monthShort(d.month)}</div>
      <div style={{ color: '#e2e8f0', fontSize: 15, fontWeight: 700 }}>{kes(d.commission, 2)} commission</div>
      <div style={{ color: '#cbd5e1', fontSize: 13.5 }}>{kes(d.transacted)} from {d.payments} payments</div>
    </div>
  );
}

export default function LedgerPage() {
  const initial = presets()['This month'];
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [tenantId, setTenantId] = useState('');
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState({ entries: [], total: 0 });
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const PAGE = 50;
  const params = { from, to, ...(tenantId ? { tenant_id: tenantId } : {}) };

  const onError = (err, msg) => {
    if (err.response?.status === 401) { localStorage.clear(); navigate('/login'); }
    if (err.response?.status === 403) navigate('/dashboard');
    setError(err.response?.data?.message || msg);
  };

  useEffect(() => {
    setLoading(true);
    setError('');
    setPage(0);
    axios.get('/api/ledger/summary', { headers, params })
      .then((r) => setSummary(r.data))
      .catch((e) => onError(e, 'Failed to load ledger'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, tenantId]);

  useEffect(() => {
    axios.get('/api/ledger/entries', { headers, params: { ...params, limit: PAGE, offset: page * PAGE } })
      .then((r) => setEntries(r.data))
      .catch((e) => onError(e, 'Failed to load ledger entries'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, tenantId, page]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await axios.get('/api/ledger/export', { headers, params, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ledger_${from}_to_${to}${tenantId ? `_tenant${tenantId}` : ''}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      onError(e, 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const activePreset = Object.entries(presets()).find(([, p]) => p.from === from && p.to === to)?.[0];
  const totals = summary?.totals;
  const tenants = summary?.byTenant || [];
  const tenantName = tenantId ? tenants.find((t) => String(t.id) === String(tenantId))?.name : null;
  const pages = Math.ceil((entries.total || 0) / PAGE);

  return (
    <div style={{ minHeight: '100vh' }}>
      <Navbar user={user} />
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }`}</style>

      <div className="mw-page" style={{ padding: '32px 24px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ color: '#0f172a', fontSize: 26, fontWeight: 700, margin: 0 }}>Master Ledger</h1>
          <p style={{ color: '#475569', fontSize: 14, marginTop: 6 }}>Commission earned from payments made on tenant sites</p>
        </div>

        {/* Filters — one row */}
        <div data-tour="ledger-filters" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          {Object.entries(presets()).map(([label, p]) => (
            <button key={label} onClick={() => { setFrom(p.from); setTo(p.to); }}
              style={{ background: activePreset === label ? '#1a5fa4' : '#ffffff', border: `1px solid ${activePreset === label ? '#1a5fa4' : '#94a3b8'}`, borderRadius: 8, padding: '8px 12px', color: activePreset === label ? '#ffffff' : '#0f172a', fontWeight: 600, fontSize: 14.5, cursor: 'pointer' }}>
              {label}
            </button>
          ))}
          <input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} style={input} aria-label="From" />
          <span style={{ color: '#cbd5e1' }}>to</span>
          <input type="date" value={to} min={from} max={todayEAT()} onChange={(e) => e.target.value && setTo(e.target.value)} style={input} aria-label="To" />
          <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} style={input}>
            <option value="" style={{ background: '#0f172a' }}>All tenants</option>
            {(summary?.byTenant || []).map((t) => <option key={t.id} value={t.id} style={{ background: '#0f172a' }}>{t.name}</option>)}
          </select>
          <button data-tour="ledger-export" onClick={exportCsv} disabled={exporting || !totals?.payments}
            style={{ marginLeft: 'auto', background: 'linear-gradient(135deg, #0891b2, #0e7490)', border: 'none', borderRadius: 10, padding: '9px 16px', color: '#fff', fontSize: 14.5, fontWeight: 600, cursor: 'pointer', opacity: !totals?.payments ? 0.5 : 1 }}>
            {exporting ? 'Exporting…' : '⬇ Export CSV'}
          </button>
        </div>

        {error && (
          <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 10, padding: '14px 18px', color: '#f87171', marginBottom: 24 }}>{error}</div>
        )}

        {summary && (
          <div style={{ display: 'grid', gap: 20, opacity: loading ? 0.6 : 1, animation: 'fadeIn 0.4s ease' }}>
            {/* Headline numbers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 20 }}>
              {[
                ['Commission earned', kes(totals.commission, 2), true],
                ['Transacted on tenant sites', kes(totals.transacted)],
                ['Payments', totals.payments.toLocaleString()],
                ['Tenants with payments', totals.tenants],
              ].map(([label, value, hero]) => (
                <div key={label} style={panel}>
                  <div style={{ fontSize: 12.5, color: '#cbd5e1', letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 600, marginBottom: 10 }}>{label}</div>
                  <div style={{ fontSize: hero ? 26 : 22, fontWeight: 800, color: hero ? '#e2e8f0' : '#cbd5e1', whiteSpace: 'nowrap' }}>{value}</div>
                </div>
              ))}
            </div>

            {/* Commission by month */}
            {summary.monthly.length > 1 && (
              <div style={panel}>
                <h3 style={{ color: '#ffffff', fontSize: 19, fontWeight: 700, margin: '0 0 16px' }}>Commission by month</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={summary.monthly} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="rgba(148,163,184,0.25)" />
                    <XAxis dataKey="month" tickFormatter={monthShort} tick={{ fill: '#f1f5f9', fontSize: 14, fontWeight: 600 }} axisLine={{ stroke: 'rgba(148,163,184,0.2)' }} tickLine={false} />
                    <YAxis tickFormatter={compact} tick={{ fill: '#f1f5f9', fontSize: 14, fontWeight: 600 }} axisLine={false} tickLine={false} width={44} />
                    <Tooltip content={<MonthTooltip />} cursor={{ fill: 'rgba(56,189,248,0.06)' }} />
                    <Bar dataKey="commission" fill={BAR} maxBarSize={24} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* By tenant */}
            <div style={{ ...panel, padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '18px 24px', borderBottom: '1px solid rgba(56,189,248,0.1)', display: 'flex', justifyContent: 'space-between' }}>
                <h3 style={{ color: '#ffffff', fontSize: 19, fontWeight: 700, margin: 0 }}>By tenant</h3>
                <span style={{ color: '#cbd5e1', fontSize: 13.5 }}>Click a tenant to see only its entries</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(56,189,248,0.1)' }}>
                      <th style={th}>Tenant</th><th style={{ ...th, ...num }}>Current rate</th><th style={{ ...th, ...num }}>Payments</th>
                      <th style={{ ...th, ...num }}>Transacted</th><th style={{ ...th, ...num }}>Commission</th><th style={{ ...th, ...num }}>Share</th><th style={th}>Last payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tenants.map((t) => (
                      <tr key={t.id} onClick={() => setTenantId(tenantId === String(t.id) ? '' : String(t.id))}
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', cursor: 'pointer', background: tenantId === String(t.id) ? 'rgba(56,189,248,0.06)' : 'transparent' }}>
                        <td style={{ ...td, color: '#e2e8f0', fontWeight: 600 }}>
                          {t.name}{!t.active && <span style={{ color: '#f87171', fontWeight: 400, fontSize: 12.5 }}> · inactive</span>}
                          <div style={{ color: '#cbd5e1', fontWeight: 400, fontSize: 12.5 }}>{t.subdomain}.smartwater.co.ke</div>
                        </td>
                        <td style={{ ...td, ...num }}>{t.current_rate}%</td>
                        <td style={{ ...td, ...num }}>{t.payments}</td>
                        <td style={{ ...td, ...num }}>{kes(t.transacted)}</td>
                        <td style={{ ...td, ...num, color: '#e2e8f0', fontWeight: 700 }}>{kes(t.commission, 2)}</td>
                        <td style={{ ...td, ...num }}>{totals.commission ? `${Math.round((t.commission / totals.commission) * 100)}%` : '—'}</td>
                        <td style={td}>{t.last_payment || '—'}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: '1px solid rgba(56,189,248,0.2)' }}>
                      <td style={{ ...td, color: '#e2e8f0', fontWeight: 700 }}>Total</td><td style={td} />
                      <td style={{ ...td, ...num, color: '#e2e8f0', fontWeight: 700 }}>{totals.payments}</td>
                      <td style={{ ...td, ...num, color: '#e2e8f0', fontWeight: 700 }}>{kes(totals.transacted)}</td>
                      <td style={{ ...td, ...num, color: '#e2e8f0', fontWeight: 700 }}>{kes(totals.commission, 2)}</td><td style={td} /><td style={td} />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Entries */}
            <div style={{ ...panel, padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '18px 24px', borderBottom: '1px solid rgba(56,189,248,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <h3 style={{ color: '#ffffff', fontSize: 19, fontWeight: 700, margin: 0 }}>
                  Entries{tenantName ? ` · ${tenantName}` : ''} <span style={{ color: '#cbd5e1', fontWeight: 400, fontSize: 14.5 }}>({entries.total})</span>
                </h3>
                {pages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#cbd5e1', fontSize: 14.5 }}>
                    <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} style={{ ...input, padding: '4px 10px', cursor: page === 0 ? 'default' : 'pointer' }}>‹</button>
                    Page {page + 1} of {pages}
                    <button disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} style={{ ...input, padding: '4px 10px', cursor: page + 1 >= pages ? 'default' : 'pointer' }}>›</button>
                  </div>
                )}
              </div>
              {entries.entries.length === 0 ? (
                <p style={{ color: '#cbd5e1', padding: '32px 24px', margin: 0, textAlign: 'center' }}>No commission entries in this period</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(56,189,248,0.1)' }}>
                        <th style={th}>Date (EAT)</th><th style={th}>Tenant</th><th style={th}>Meter</th><th style={th}>Phone</th><th style={th}>M-Pesa ref</th>
                        <th style={{ ...th, ...num }}>Amount</th><th style={{ ...th, ...num }}>Rate</th><th style={{ ...th, ...num }}>Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.entries.map((e) => (
                        <tr key={e.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td style={td}>{e.date}</td>
                          <td style={{ ...td, color: '#e2e8f0' }}>{e.tenant}</td>
                          <td style={{ ...td, fontFamily: 'monospace' }}>{e.meter}</td>
                          <td style={td}>{e.phone}</td>
                          <td style={{ ...td, fontFamily: 'monospace' }}>{e.mpesa_ref || '—'}</td>
                          <td style={{ ...td, ...num }}>{kes(e.amount)}</td>
                          <td style={{ ...td, ...num }}>{e.rate ?? '—'}%</td>
                          <td style={{ ...td, ...num, color: '#e2e8f0', fontWeight: 600 }}>{kes(e.commission, 2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
